// src/pages/Base/BasePage.tsx
import React from 'react';
import { BaseComponent } from './BaseComponent';
import { BasePageProps, BasePageState } from '../../types/base';
import Header from '@components/header';

export abstract class BasePage<
    P extends BasePageProps = BasePageProps,
    S extends BasePageState = BasePageState
> extends BaseComponent<P, S> {

    private previousPathname: string = '';

    constructor(props: P) {
        super(props);

        // 设置页面标题
        if (props.title) {
            document.title = props.title;
        }
        
        // 初始化记录当前路径
        this.previousPathname = window.location.pathname;
    }

    componentDidMount() {
        this.handlePageLoad();
        this.onPageMount();
    }

    onPageMount() {
        this.scrollToTop();
    }
    
    scrollToTop = () => {
        window.scrollTo(0, 0);
    }

    protected getInitialState(): Partial<S> {
        return {
            pageLoaded: false,
            ...super.getInitialState()
        };
    }

    // 页面加载处理
    protected handlePageLoad = async (): Promise<void> => {
        await this.handleAsyncOperation(
            async () => {
                await this.onPageLoad();
                this.safeSetState({ pageLoaded: true } as Partial<S>);
            },
            {
                loading: true,
                errorMessage: '页面加载失败'
            }
        );
    }

    // 页面刷新
    protected refreshPage = (): void => {
        this.onPageMount();
        this.setError(null);
        this.handlePageLoad();
    }

    // 子类可以重写此方法来实现页面加载逻辑
    protected async onPageLoad(): Promise<void> {
        // 默认实现，子类可以重写
    }

    // 渲染页面头部
    protected renderHeader(): React.ReactNode {
        const { title, showHeader = true } = this.props;

        // if (!showHeader) return null;

        return (
            <header className="page-header">
                <Header></Header>
                <h1>{title || '页面标题'}</h1>
            </header>
        );
    }

    // 渲染页面内容
    protected renderPageContent(): React.ReactNode {
        return this.renderContent();
    }

    // 渲染页面底部
    protected renderFooter(): React.ReactNode {
        const { showFooter = true } = this.props;

        if (!showFooter) return null;

        return (
            <footer className="page-footer">
                <p>&copy; 2024 我的应用</p>
            </footer>
        );
    }

    renderContent() {
        // 这里可以安全访问 this.state，因为 BasePageState 包含了 pageLoaded
        const { pageLoaded } = this.state as BasePageState;

        if (!pageLoaded) {
            return null;
        }

        return (
            <div className="page-container">
                {this.renderHeader()}
                <main className="page-main">
                    {this.renderPageContent()}
                </main>
                {this.renderFooter()}
            </div>
        );
    }
}